import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  UpdateIncidentalCostRequest,
  UpdateIncidentalCostUseCalculationRequest,
  UpdateTreatmentPriceAbsoluteDiscountRequest,
  UpdateTreatmentPriceBasePriceRequest,
  UpdateTreatmentPricePostExecutionReductionRequest,
  UpdateTreatmentPriceRelativeDiscountRequest,
  UpdateTreatmentPriceUseCalculationRequest,
  useRltPricingShowQuery,
  useRltPricingUpdateIncidentalCostMutation,
  useRltPricingUpdateIncidentalCostUseCalculationMutation,
  useRltPricingUpdateTreatmentPriceAbsoluteDiscountMutation,
  useRltPricingUpdateTreatmentPriceBasePriceMutation,
  useRltPricingUpdateTreatmentPricePostExecutionReductionMutation,
  useRltPricingUpdateTreatmentPriceRelativeDiscountMutation,
  useRltPricingUpdateTreatmentPriceUseCalculationMutation,
} from "@/services/backend/rlt/pricing/api/service";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ReactNode, useState } from "react";
import {
  AbsoluteDiscount,
  RelativeDiscount,
  Pricing,
  IncidentalCost,
} from "@/services/backend/rlt/pricing/types/pricing";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { Label } from "@/shared/components/ui/label";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { Field } from "@/shared/components/ui/field";
import { Checkbox } from "@/shared/components/ui/checkbox";

export function TreatmentPricesCard({
  pricingId,
  disabled = false,
  showPostExecutionReduction = false,
}: {
  pricingId: string;
  disabled?: boolean;
  showPostExecutionReduction?: boolean;
}) {
  const { data, isLoading, error } = useRltPricingShowQuery({ id: pricingId });

  if (error) {
    return <RTKQueryErrorAlert error={error} ignoreValidationError={false} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Grundbehandlung und Nebenkosten")}</CardTitle>
        {data && (
          <CardAction className="flex gap-4">
            <span className="text-sm font-medium whitespace-nowrap">
              {t("Daten aus Kalkulation:")}
            </span>
            <TreatmentPriceUseCalculationCheckbox
              pricing={data}
              disabled={disabled}
            />
            <IncidentalCostUseCalculationCheckbox
              pricing={data}
              disabled={disabled}
            />
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {isLoading && <CardContentSkeleton />}
        {data && (
          <TreatmentPriceTable>
            <TreatmentPriceBasePrice pricing={data} disabled={disabled} />
            <TreatmentPriceRelativeDiscount
              pricing={data}
              disabled={disabled}
            />
            <TreatmentPriceAbsoluteDiscount
              pricing={data}
              disabled={disabled}
            />
            <TreatmentPriceFinalPriceRow
              finalPrice={data.treatmentPrice.finalPrice}
            />
            {showPostExecutionReduction && (
              <>
                <TreatmentPricePostExecutionReduction pricing={data} />
                <TreatmentPricePostExecutionFinalPriceRow
                  postExecutionFinalPrice={
                    data.treatmentPrice.postExecutionFinalPrice
                  }
                />
              </>
            )}
            <IncidentalCosts pricing={data} />
            <ETableRow>
              <ETableCell className="font-bold" colSpan={4}>
                {t("Gesamtpreis")}
              </ETableCell>
              <ETableCellInput
                value={data.totalPrice.toFixed(2)}
                disabled
                type="number"
                className="font-bold disabled:pointer-events-auto disabled:opacity-100"
              />
              <ETableCell className="w-px font-bold">€</ETableCell>
            </ETableRow>
          </TreatmentPriceTable>
        )}
      </CardContent>
    </Card>
  );
}

function TreatmentPriceUseCalculationCheckbox({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] =
    useState<UpdateTreatmentPriceUseCalculationRequest>({
      id: pricing.id,
      value: pricing.treatmentPriceUseCalculation,
    });
  const mutation = useRltPricingUpdateTreatmentPriceUseCalculationMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      value: pricing.treatmentPriceUseCalculation,
    },
    { delay: 0 },
  );

  return (
    <Field orientation="horizontal">
      <Checkbox
        id={`${pricing.id}-treatment-price-use-calculation-checkbox`}
        checked={request.value}
        onCheckedChange={(value) => {
          setRequest({
            ...request,
            value: value as boolean,
          });
        }}
        disabled={disabled}
      />
      <Label htmlFor={`${pricing.id}-treatment-price-use-calculation-checkbox`}>
        {t("Grundbehandlung")}
      </Label>
    </Field>
  );
}

function IncidentalCostUseCalculationCheckbox({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] =
    useState<UpdateIncidentalCostUseCalculationRequest>({
      id: pricing.id,
      value: pricing.incidentalCostUseCalculation,
    });
  const mutation = useRltPricingUpdateIncidentalCostUseCalculationMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      value: pricing.incidentalCostUseCalculation,
    },
    { delay: 0 },
  );

  return (
    <Field orientation="horizontal">
      <Checkbox
        id={`${pricing.id}-incidental-cost-use-calculation-checkbox`}
        checked={request.value}
        onCheckedChange={(value) =>
          setRequest({
            ...request,
            value: value as boolean,
          })
        }
        disabled={disabled}
      />
      <Label htmlFor={`${pricing.id}-incidental-cost-use-calculation-checkbox`}>
        {t("Nebenkosten")}
      </Label>
    </Field>
  );
}

function TreatmentPriceBasePrice({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateTreatmentPriceBasePriceRequest>({
    id: pricing.id,
    value: pricing.treatmentPrice.basePrice,
  });
  const mutation = useRltPricingUpdateTreatmentPriceBasePriceMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      value: pricing.treatmentPrice.basePrice,
    },
    {
      delay: 1000,
      skip: pricing.treatmentPriceUseCalculation,
    },
  );
  useRefEffect(pricing.treatmentPrice.basePrice, () =>
    setRequest({
      id: pricing.id,
      value: pricing.treatmentPrice.basePrice,
    }),
  );

  return (
    <TreatmentPriceBasePriceRow
      basePrice={request.value}
      onBasePriceChange={(value) => {
        setRequest({ ...request, value });
      }}
      disabled={pricing.treatmentPriceUseCalculation || disabled}
    />
  );
}

function TreatmentPriceRelativeDiscount({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] =
    useState<UpdateTreatmentPriceRelativeDiscountRequest>({
      id: pricing.id,
      active: pricing.treatmentPrice.relativeDiscount.active,
      percentage: pricing.treatmentPrice.relativeDiscount.percentage,
    });
  const mutation = useRltPricingUpdateTreatmentPriceRelativeDiscountMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      active: pricing.treatmentPrice.relativeDiscount.active,
      percentage: pricing.treatmentPrice.relativeDiscount.percentage,
    },
    { delay: 1000 },
  );

  return (
    <TreatmentPriceRelativeDiscountRow
      basePrice={pricing.treatmentPrice.basePrice}
      relativeDiscount={request}
      onRelativeDiscountChange={(relativeDiscount) => {
        setRequest({ ...request, ...relativeDiscount });
      }}
      disabled={disabled}
    />
  );
}

function TreatmentPriceAbsoluteDiscount({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] =
    useState<UpdateTreatmentPriceAbsoluteDiscountRequest>({
      id: pricing.id,
      active: pricing.treatmentPrice.absoluteDiscount.active,
      amount: pricing.treatmentPrice.absoluteDiscount.amount,
    });
  const mutation = useRltPricingUpdateTreatmentPriceAbsoluteDiscountMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      active: pricing.treatmentPrice.absoluteDiscount.active,
      amount: pricing.treatmentPrice.absoluteDiscount.amount,
    },
    { delay: 1000 },
  );

  return (
    <TreatmentPriceAbsoluteDiscountRow
      absoluteDiscount={request}
      onAbsoluteDiscountChange={(absoluteDiscount) => {
        setRequest({ ...request, ...absoluteDiscount });
      }}
      disabled={disabled}
    />
  );
}

function TreatmentPriceTable({ children }: { children: ReactNode }) {
  return (
    <ETable>
      <ETableBody>{children}</ETableBody>
    </ETable>
  );
}

function TreatmentPriceBasePriceRow({
  basePrice,
  onBasePriceChange,
  disabled = false,
}: {
  basePrice: number;
  onBasePriceChange: (basePrice: number) => void;
  disabled?: boolean;
}) {
  return (
    <ETableRow>
      <ETableCell colSpan={4}>{t("Grundbehandlung")}</ETableCell>
      <ETableCellDecimalInput
        value={basePrice}
        onValueChange={onBasePriceChange}
        fieldName="treatmentPrice.basePrice"
        disabled={disabled}
        className="disabled:pointer-events-auto disabled:opacity-100"
      />
      <ETableCell className="w-px">€</ETableCell>
    </ETableRow>
  );
}

function TreatmentPriceRelativeDiscountRow({
  basePrice,
  relativeDiscount,
  onRelativeDiscountChange,
  disabled = true,
}: {
  basePrice: number;
  relativeDiscount: RelativeDiscount;
  onRelativeDiscountChange: (relativeDiscount: RelativeDiscount) => void;
  disabled?: boolean;
}) {
  return (
    <ETableRow>
      <ETableCell>{t("Verkaufsrabatt")}</ETableCell>
      <ETableCellCheckbox
        checked={relativeDiscount.active}
        onCheckedChange={(checked) =>
          onRelativeDiscountChange({
            ...relativeDiscount,
            active: Boolean(checked),
          })
        }
        disabled={disabled}
        label={t("Aktiv")}
      />
      <ETableCellInput
        type="number"
        value={relativeDiscount.percentage}
        onChange={(e) =>
          onRelativeDiscountChange({
            ...relativeDiscount,
            percentage: Number(e.target.value),
          })
        }
        fieldName="treatmentPrice.relativeDiscount.percentage"
        disabled={disabled}
      />
      <ETableCell className="w-px">%</ETableCell>
      <ETableCellInput
        value={
          relativeDiscount.active
            ? (basePrice * (relativeDiscount.percentage / 100)).toFixed(2)
            : (0).toFixed(2)
        }
        disabled
        type="number"
        className="disabled:pointer-events-auto disabled:opacity-100"
      />
      <ETableCell className="w-px">€</ETableCell>
    </ETableRow>
  );
}

function TreatmentPriceAbsoluteDiscountRow({
  absoluteDiscount,
  onAbsoluteDiscountChange,
  disabled = true,
}: {
  absoluteDiscount: AbsoluteDiscount;
  onAbsoluteDiscountChange: (absoluteDiscount: AbsoluteDiscount) => void;
  disabled?: boolean;
}) {
  return (
    <ETableRow>
      <ETableCell>{t("Sonderrabatt")}</ETableCell>
      <ETableCellCheckbox
        checked={absoluteDiscount.active}
        onCheckedChange={(checked) =>
          onAbsoluteDiscountChange({
            ...absoluteDiscount,
            active: Boolean(checked),
          })
        }
        disabled={disabled}
        label={t("Aktiv")}
      />
      <ETableCellDecimalInput
        value={absoluteDiscount.active ? absoluteDiscount.amount : 0}
        onValueChange={(amount) =>
          onAbsoluteDiscountChange({ ...absoluteDiscount, amount })
        }
        fieldName="treatmentPrice.absoluteDiscount.amount"
        disabled={disabled}
        colSpan={3}
      />
      <ETableCell className="w-px">€</ETableCell>
    </ETableRow>
  );
}

function TreatmentPriceFinalPriceRow({ finalPrice }: { finalPrice: number }) {
  return (
    <ETableRow>
      <ETableCell colSpan={4} className="font-bold">
        {t("Grundbehandlung abzgl. Rabatte")}
      </ETableCell>
      <ETableCellInput
        value={finalPrice.toFixed(2)}
        disabled
        type="number"
        className="font-bold disabled:pointer-events-auto disabled:opacity-100"
        fieldName="treatmentPrice.finalPrice"
      />
      <ETableCell className="w-px font-bold">€</ETableCell>
    </ETableRow>
  );
}

function TreatmentPricePostExecutionReduction({
  pricing,
}: {
  pricing: Pricing;
}) {
  const [request, setRequest] =
    useState<UpdateTreatmentPricePostExecutionReductionRequest>({
      id: pricing.id,
      label: pricing.treatmentPrice.postExecutionReduction.label,
      amount: pricing.treatmentPrice.postExecutionReduction.amount,
    });
  const mutation =
    useRltPricingUpdateTreatmentPricePostExecutionReductionMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      label: pricing.treatmentPrice.postExecutionReduction.label,
      amount: pricing.treatmentPrice.postExecutionReduction.amount,
    },
    {
      delay: 1000,
    },
  );

  return (
    <TreatmentPricePostExecutionReductionRow
      reduction={request}
      onReductionChange={(reduction) =>
        setRequest({ ...request, ...reduction })
      }
    />
  );
}

function TreatmentPricePostExecutionReductionRow({
  reduction,
  onReductionChange,
}: {
  reduction: { label: string; amount: number };
  onReductionChange: (reduction: { label: string; amount: number }) => void;
}) {
  return (
    <ETableRow>
      <ETableCell>{t("Nachlass")}</ETableCell>
      <ETableCellInput
        value={reduction.label}
        onChange={(e) =>
          onReductionChange({ ...reduction, label: e.target.value })
        }
        placeholder={t("Grund")}
        colSpan={3}
      />
      <ETableCellDecimalInput
        value={reduction.amount}
        onValueChange={(amount) => onReductionChange({ ...reduction, amount })}
        fieldName="treatmentPrice.postExecutionReduction.amount"
      />
      <ETableCell className="w-px">€</ETableCell>
    </ETableRow>
  );
}

function TreatmentPricePostExecutionFinalPriceRow({
  postExecutionFinalPrice,
}: {
  postExecutionFinalPrice: number;
}) {
  return (
    <ETableRow>
      <ETableCell colSpan={4} className="font-bold">
        {t("Grundbehandlung abzgl. Nachlass")}
      </ETableCell>
      <ETableCellInput
        value={postExecutionFinalPrice.toFixed(2)}
        disabled
        type="number"
        className="font-bold disabled:pointer-events-auto disabled:opacity-100"
      />
      <ETableCell className="w-px font-bold">€</ETableCell>
    </ETableRow>
  );
}

function IncidentalCosts({ pricing }: { pricing: Pricing }) {
  const [request, setRequest] = useState<UpdateIncidentalCostRequest>({
    id: pricing.id,
    incidentalCost: pricing.incidentalCost,
  });
  const mutation = useRltPricingUpdateIncidentalCostMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: pricing.id,
      incidentalCost: pricing.incidentalCost,
    },
    {
      delay: 1000,
      skip: pricing.incidentalCostUseCalculation,
    },
  );
  useRefEffect(pricing.incidentalCost, () =>
    setRequest({
      id: pricing.id,
      incidentalCost: pricing.incidentalCost,
    }),
  );

  return (
    <IncidentalCostRows
      incidentalCost={request.incidentalCost}
      onIncidentalCostChange={(incidentalCost) =>
        setRequest({ ...request, incidentalCost })
      }
      disabled={pricing.incidentalCostUseCalculation}
    />
  );
}

function IncidentalCostRows({
  incidentalCost,
  onIncidentalCostChange,
  disabled,
}: {
  incidentalCost: IncidentalCost;
  onIncidentalCostChange: (incidentalCost: IncidentalCost) => void;
  disabled?: boolean;
}) {
  const setMaterialFlatRate = (materialFlatRate: number) => {
    const totalAmount =
      materialFlatRate + incidentalCost.travelTime + incidentalCost.expenses;
    onIncidentalCostChange({
      ...incidentalCost,
      materialFlatRate,
      totalAmount,
    });
  };

  const setTravelTime = (travelTime: number) => {
    const totalAmount =
      incidentalCost.materialFlatRate + travelTime + incidentalCost.expenses;
    onIncidentalCostChange({
      ...incidentalCost,
      travelTime,
      totalAmount,
    });
  };

  const setExpenses = (expenses: number) => {
    const totalAmount =
      incidentalCost.materialFlatRate + incidentalCost.travelTime + expenses;
    onIncidentalCostChange({
      ...incidentalCost,
      expenses,
      totalAmount,
    });
  };

  return (
    <>
      <ETableRow>
        <ETableCell colSpan={4}>{t("Materialkosten")}</ETableCell>
        <ETableCellDecimalInput
          value={incidentalCost.materialFlatRate}
          onValueChange={setMaterialFlatRate}
          fieldName="incidentalCost.materialFlatRate"
          disabled={disabled}
          className="disabled:pointer-events-auto disabled:opacity-100"
        />
        <ETableCell className="w-px">€</ETableCell>
      </ETableRow>
      <ETableRow>
        <ETableCell colSpan={4}>{t("Fahrtkosten")}</ETableCell>
        <ETableCellDecimalInput
          value={incidentalCost.travelTime}
          onValueChange={setTravelTime}
          fieldName="incidentalCost.travelTime"
          disabled={disabled}
          className="disabled:pointer-events-auto disabled:opacity-100"
        />
        <ETableCell className="w-px">€</ETableCell>
      </ETableRow>
      <ETableRow>
        <ETableCell colSpan={4}>{t("Spesen")}</ETableCell>
        <ETableCellDecimalInput
          value={incidentalCost.expenses}
          onValueChange={setExpenses}
          fieldName="incidentalCost.expenses"
          disabled={disabled}
          className="disabled:pointer-events-auto disabled:opacity-100"
        />
        <ETableCell className="w-px">€</ETableCell>
      </ETableRow>
    </>
  );
}
